import React, { FC, memo, useMemo } from 'react';
import styles from './shared-layout.module.css';

import {
    ImageTagViewModel,
    ProductAccordion,
    ProductImageViewModel,
    ProductPriceViewModel,
} from '~/lib/data-contract';
import ImageGallery from '../image-gallery/image-gallery.componen';
import { ProductTagsLegacy, Accordion, RawHtml, Typography, Usp } from '~/shared/components';
import { useFrame } from '~/shared/utils';

import { addResizeVariablesToProductImage } from '~/shared/utils/product';
import Head from 'next/head';
import { PriceGroup } from '~/shared/components/price-group/price-group.component';
import { Container } from '../../container/container.component';

type Props = {
    children?: React.ReactNode;
    productData?: {
        images?: ProductImageViewModel[];
        accordions?: ProductAccordion[];
        title?: string;
        subtitle?: string;
        priceDetails?: ProductPriceViewModel;
        imageTags?: ImageTagViewModel[];
    };
};

const ProductDetailsSharedLayout: FC<Props> = memo((props) => {
    const { children, productData } = props;
    const { images, accordions, title, subtitle, priceDetails, imageTags } = productData || {};

    const { data: frame } = useFrame();

    // Memoize expensive image processing to prevent re-computation
    const resizedFirstProductImage = useMemo(() => 
        addResizeVariablesToProductImage(images?.[0]?.url), 
        [images]
    );

    // Memoize accordion items to prevent re-creation on every render
    const accordionItems = useMemo(() => 
        accordions?.map(({ title = '', value = '' }, index) => ({
            initAsOpen: index === 0,
            id: `${title}-${index}`,
            title,
            content: <RawHtml html={value} />,
        })), 
        [accordions]
    );

    // Memoize USP list to prevent re-creation when frame doesn't change
    const uspListItems = useMemo(() => 
        frame?.usp?.uspList?.map((uspItem, index) => (
            <li key={index}>
                <Usp
                    uspItem={uspItem}
                    shade="paragraph"
                    textDecoration="underline"
                />
            </li>
        )), 
        [frame?.usp?.uspList]
    );

    // Memoize PriceGroup props to prevent object recreation
    const priceGroupProps = useMemo(() => ({
        ...priceDetails,
        variant: "display4" as const,
        imageTags,
        isPDP: true,
    }), [priceDetails, imageTags]);

    return (
        <>
            {resizedFirstProductImage && (
                <Head>
                    <meta name="image" content={resizedFirstProductImage} />
                    <meta property="og:image" content={resizedFirstProductImage} />
                </Head>
            )}
            <Container spacingTop="none" spacingBottom="large" hasGutter="small">
                <div className={styles.root}>
                    <div className={styles.imageGallery}>
                        <div className={styles.productTagsTop}>
                            <ProductTagsLegacy
                                productTags={imageTags}
                                selectFrom="top"
                                layout="spaced-list"
                            />
                        </div>
                        <ImageGallery images={images} />
                    </div>
                    <div className={styles.sidebar}>
                        <div className={styles.sidebarTopContent}>
                            <div className={styles.header}>
                                {title && (
                                    <Typography variant="display2" as="h1">
                                        {title}
                                    </Typography>
                                )}
                                {subtitle && (
                                    <Typography variant="body" as="div">
                                        {subtitle}
                                    </Typography>
                                )}
                                {imageTags && (
                                    <ProductTagsLegacy
                                        productTags={imageTags}
                                        layout="list"
                                        selectFrom="bottom"
                                    />
                                )}
                                {priceDetails && (
                                    <PriceGroup {...priceGroupProps} />
                                )}
                            </div>
                            <div className={styles.actionsWrapper}>{children}</div>
                            {frame?.usp?.uspList?.length ? (
                                <ul className={styles.uspList}>
                                    {uspListItems}
                                </ul>
                            ) : null}
                        </div>
                        <div className={styles.stickyContent}>
                            <Accordion
                                multiple
                                items={accordionItems}
                            />
                        </div>
                    </div>
                </div>
            </Container>
        </>
    );
});

ProductDetailsSharedLayout.displayName = 'ProductDetailsSharedLayout';
export default ProductDetailsSharedLayout;
